import React, { Suspense, ComponentType } from 'react';
import { View, ActivityIndicator, StyleSheet } from 'react-native';
import useTheme from '../../hooks/useTheme';

// Loading component for lazy loaded screens
export function ScreenLoader() {
  const { colors } = useTheme();

  return (
    <View style={[styles.container, { backgroundColor: colors.background }]}>
      <ActivityIndicator size="large" color={colors.primary} />
    </View>
  );
}

// Helper function to create lazy loaded screens with Suspense
export function createLazyScreen<T extends ComponentType<any>>(
  importFunc: () => Promise<{ default: T }>
): React.FC<React.ComponentProps<T>> {
  const LazyComponent = React.lazy(importFunc);

  return (props: React.ComponentProps<T>) => (
    <Suspense fallback={<ScreenLoader />}>
      <LazyComponent {...props} />
    </Suspense>
  );
}

// Batch lazy loading for multiple screens
export function lazyLoadScreens<T extends Record<string, () => Promise<{ default: ComponentType<any> }>>>(
  screens: T
): { [K in keyof T]: React.FC } {
  const lazyScreens: any = {};
  
  for (const [name, importFunc] of Object.entries(screens)) {
    lazyScreens[name] = createLazyScreen(importFunc);
  }
  
  return lazyScreens;
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
});